import { useMemo, type ReactNode } from 'react';
import { CheckCircle, ClipboardList, Eye, Target } from 'lucide-react';
import { brandColors } from '@/design';
import type { DayFocus, TaskPlan } from '../../types';
import { isFocusFullyCompleted, isTaskListFullyCompleted } from '../../utils/todoSwimLanes';

/**
 * Past-day Schedule view (v1).
 *
 * Replaces the Today / This week / Later lanes when the user clicks a
 * previous date on the `ScheduleDateStrip`. Two retrospective sections:
 *
 *   - "Completed on {date}" — tasks/focuses whose `completedAt` falls on
 *     that calendar day, struck through with a check mark.
 *   - "Created on {date}" — tasks/focuses whose `createdAt` falls on that
 *     day, rendered with their **current** completion state. So a task
 *     created on Mon and completed on Wed shows as "still open" if you're
 *     viewing Tuesday and "completed" if you're viewing Wednesday.
 *
 * A task that was both created and completed the same day appears in both
 * sections by design — the mental models ("what landed on my plate") and
 * ("what did I close") are different, even when the two events overlap.
 *
 * Read-only: no drag/drop. The Schedule date strip stays interactive above.
 */

type Row =
  | { kind: 'task'; id: string; task: TaskPlan }
  | { kind: 'day_focus'; id: string; focus: DayFocus; task: TaskPlan };

interface Props {
  date: Date;
  tasks: TaskPlan[];
  focuses: DayFocus[];
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime();
}

function tsToDate(ts: unknown): Date | null {
  if (!ts) return null;
  const t = ts as { toDate?: () => Date; seconds?: number };
  if (typeof t.toDate === 'function') {
    const d = t.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  return null;
}

function formatDate(d: Date): string {
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${day} ${month} ${d.getDate()}`;
}

function buildRows(tasks: TaskPlan[], focuses: DayFocus[]): Row[] {
  return [
    ...focuses.map((focus) => ({
      kind: 'day_focus' as const,
      id: focus.id,
      focus,
      task: dayFocusToTaskPlanShim(focus),
    })),
    ...tasks.map((task) => ({ kind: 'task' as const, id: task.id, task })),
  ];
}

/** Lightweight shim — same shape used in TodosPage to render focuses through TaskCard. */
function dayFocusToTaskPlanShim(focus: DayFocus): TaskPlan {
  return {
    id: focus.id,
    userId: focus.userId,
    sessionId: focus.sessionId,
    title: focus.title,
    summary: focus.summary ?? '',
    status: focus.status,
    actionItems: focus.actionItems,
    swimLane: focus.swimLane,
    projectId: focus.projectId,
    dueDate: focus.dueDate,
    createdAt: focus.createdAt,
    updatedAt: focus.updatedAt,
    completedAt: focus.completedAt,
  };
}

export function PastDaySection({ date, tasks, focuses }: Props) {
  const rows = useMemo(() => buildRows(tasks, focuses), [tasks, focuses]);

  const completedOnDay = useMemo(
    () =>
      rows.filter((row) => {
        const isDone = row.kind === 'task'
          ? isTaskListFullyCompleted(row.task)
          : isFocusFullyCompleted(row.focus);
        if (!isDone) return false;
        const completedAt = tsToDate(row.task.completedAt);
        if (!completedAt) return false;
        return isSameLocalDay(completedAt, date);
      }),
    [rows, date]
  );

  const createdOnDay = useMemo(
    () =>
      rows.filter((row) => {
        const createdAt = tsToDate(row.task.createdAt);
        if (!createdAt) return false;
        return isSameLocalDay(createdAt, date);
      }),
    [rows, date]
  );

  const hasContent = completedOnDay.length > 0 || createdOnDay.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div
        className="rounded-2xl px-4 py-3"
        style={{
          background: 'var(--color-surface)',
          border: '1px dashed var(--color-border)',
        }}
      >
        <p className="text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] font-semibold">
          Looking back
        </p>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1 leading-snug">
          Showing what was created and completed on <strong className="text-[var(--color-text-primary)]">{formatDate(date)}</strong>. Click <strong className="text-[var(--color-text-primary)]">Today</strong> on the strip above to return to the live lanes.
        </p>
      </div>

      {!hasContent ? (
        <div
          className="rounded-2xl px-4 py-8 text-center"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
          }}
        >
          <p className="text-sm text-[var(--color-text-muted)]">
            No lists were created or completed on {formatDate(date)}.
          </p>
        </div>
      ) : null}

      {completedOnDay.length > 0 ? (
        <Section
          icon={<CheckCircle size={16} style={{ color: 'var(--color-success)' }} />}
          title={`Completed on ${formatDate(date)}`}
          subtitle={`${completedOnDay.length} list${completedOnDay.length === 1 ? '' : 's'} closed`}
          rows={completedOnDay}
          variant="completed"
        />
      ) : null}

      {createdOnDay.length > 0 ? (
        <Section
          icon={<ClipboardList size={16} style={{ color: brandColors.lightBlue }} />}
          title={`Created on ${formatDate(date)}`}
          subtitle={`${createdOnDay.length} list${createdOnDay.length === 1 ? '' : 's'} captured`}
          rows={createdOnDay}
          variant="created"
        />
      ) : null}
    </div>
  );
}

function Section({
  icon,
  title,
  subtitle,
  rows,
  variant,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  rows: Row[];
  variant: 'created' | 'completed';
}) {
  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center gap-2">
        <span className="shrink-0">{icon}</span>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h2>
        <span className="text-[11px] text-[var(--color-text-muted)]">{subtitle}</span>
      </header>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <RetroCard key={`${row.kind}:${row.id}`} row={row} variant={variant} />
        ))}
      </div>
    </section>
  );
}

function RetroCard({ row, variant }: { row: Row; variant: 'created' | 'completed' }) {
  const isDayFocus = row.kind === 'day_focus';
  const isComplete =
    row.kind === 'task'
      ? isTaskListFullyCompleted(row.task)
      : isFocusFullyCompleted(row.focus);
  const total = row.task.actionItems.length;
  const done = row.task.actionItems.filter((it) => it.completed || it.status === 'done').length;
  const completedAt = tsToDate(row.task.completedAt);

  return (
    <div
      className="rounded-xl px-4 py-3 flex items-start gap-3"
      style={{
        background: 'var(--color-surface)',
        border: `1px solid ${isComplete && variant === 'completed' ? `${brandColors.lightBlue}33` : 'var(--color-border)'}`,
        opacity: variant === 'completed' && isComplete ? 0.92 : 1,
      }}
    >
      <span aria-hidden className="shrink-0 mt-0.5">
        {isDayFocus ? (
          <Eye size={14} style={{ color: isComplete ? 'var(--color-success)' : brandColors.teal }} />
        ) : (
          <Target size={14} style={{ color: isComplete ? 'var(--color-success)' : brandColors.darkBlue }} />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm font-semibold truncate ${
            isComplete ? 'text-[var(--color-text-muted)] line-through' : 'text-[var(--color-text-primary)]'
          }`}
          title={row.task.title}
        >
          {row.task.title}
        </p>
        {total > 0 ? (
          <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
            {done}/{total} done
            {variant === 'completed' && completedAt
              ? ` · closed at ${completedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : null}
          </p>
        ) : variant === 'completed' && completedAt ? (
          <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
            Closed at {completedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        ) : null}
      </div>
      {isComplete ? (
        <CheckCircle
          size={16}
          style={{ color: 'var(--color-success)', flexShrink: 0 }}
          aria-label="Completed"
        />
      ) : null}
    </div>
  );
}
